import axios from "axios";

const MONGO_ID_REGEX = /^[a-f0-9]{24}$/i;

export function formatNumberDe(value) {
  return Number(value || 0).toLocaleString("de-DE");
}

export function normalizeEntityId(value) {
  if (!value) return "";
  if (typeof value === "object") {
    const nestedId = value?._id || value?.id || value?.$oid;
    if (typeof nestedId === "object") {
      return `${nestedId?.$oid || nestedId?.id || nestedId?._id || ""}`.trim();
    }
    return `${nestedId || ""}`.trim();
  }
  return `${value}`.trim();
}

export function syncSelectionWithLatestItems(
  selectedIds = [],
  latestItems = [],
  getKey
) {
  if (!selectedIds.length || typeof getKey !== "function") return [];
  const latestById = new Map(
    (latestItems || [])
      .map(item => [normalizeEntityId(getKey(item)), item])
      .filter(([id]) => Boolean(id))
  );
  return selectedIds
    .map(id => latestById.get(normalizeEntityId(id)))
    .filter(Boolean);
}

export function normalizeLabel(label) {
  return `${label || ""}`.trim().toLowerCase();
}

export function getNormalizedIdList(values = [], getId) {
  return [
    ...new Set(
      (values || [])
        .map(value => getId(value))
        .filter(Boolean)
        .map(value => `${value}`.trim())
        .filter(Boolean)
    )
  ];
}

export function getSuggestionMapKey(itemOrId, suggestionType, getId) {
  const itemKey = getId(itemOrId);
  if (!itemKey || !suggestionType) return "";
  return `${suggestionType}::${itemKey}`;
}

export function getLabelFromObject(value, labelKeys = []) {
  if (!value || typeof value !== "object") return "";
  const key = (labelKeys || []).find(candidate => {
    return `${value?.[candidate] || ""}`.trim();
  });
  if (!key) return "";
  return `${value?.[key] || ""}`.trim();
}

export function resolveLabelFromValue(
  value,
  {
    resolveTitleById = () => "",
    getId = normalizeEntityId,
    labelKeys = ["title", "name", "label", "short_title"],
    allowRawString = false,
    ignoreMongoIdRawString = true
  } = {}
) {
  if (!value) return "";

  const normalizedId = getId(value);
  const labelFromId = normalizedId
    ? `${resolveTitleById(normalizedId) || ""}`.trim()
    : "";
  if (labelFromId) return labelFromId;

  const labelFromObject = getLabelFromObject(value, labelKeys);
  if (labelFromObject) return labelFromObject;

  if (typeof value !== "string" || !allowRawString) return "";
  const rawLabel = value.trim();
  if (!rawLabel) return "";
  if (ignoreMongoIdRawString && MONGO_ID_REGEX.test(rawLabel)) return "";
  return rawLabel;
}

export function extractResolvedLabels(values = [], options = {}) {
  const labels = (values || [])
    .map(value => resolveLabelFromValue(value, options))
    .filter(Boolean);
  return [...new Set(labels)];
}

export function getSuggestionsForItemByKey(map, key) {
  if (!key) return [];
  return map?.[key] || [];
}

export function getSuggestionErrorForItemByKey(map, key) {
  if (!key) return "";
  return map?.[key] || "";
}

export function getAppliedStateForItemByKey(map, key) {
  if (!key) return false;
  return Boolean(map?.[key]);
}

export function isItemLoadingSuggestionsByKey(loadingKey, key) {
  if (!key) return false;
  return loadingKey === key;
}

export function countSuggestionItems(
  dialogItems = [],
  suggestionType,
  getSuggestions
) {
  return (dialogItems || []).reduce((acc, item) => {
    return acc + (getSuggestions(item, suggestionType) || []).length;
  }, 0);
}

export function isValidBatchMode(mode) {
  return ["add", "remove"].includes(mode);
}

export function togglePendingEntry(pendingEntries = [], { tag, type }) {
  const normalizedTag = `${tag || ""}`.trim();
  if (!normalizedTag || !type) return pendingEntries;

  const isSameEntry = pendingEntries.some(
    entry => entry.tag === normalizedTag && entry.type === type
  );
  const rest = pendingEntries.filter(entry => entry.tag !== normalizedTag);
  return isSameEntry ? rest : [...rest, { tag: normalizedTag, type }];
}

export function removePendingEntry(pendingEntries = [], tag) {
  const normalizedTag = `${tag || ""}`.trim();
  if (!normalizedTag) return pendingEntries;
  return pendingEntries.filter(entry => entry.tag !== normalizedTag);
}

export async function runBulkTagUpdateRequest({
  apiUrl,
  ids = [],
  field,
  tagIds = [],
  action,
  headers,
  params,
  getId = value => value
}) {
  const normalizedIds = getNormalizedIdList(ids, getId);
  const normalizedTagIds = getNormalizedIdList(tagIds, getId);
  if (!apiUrl || !normalizedIds.length || !normalizedTagIds.length)
    return false;

  const config = {
    headers,
    params
  };

  await axios.post(
    apiUrl,
    {
      ids: normalizedIds,
      field,
      tagIds: normalizedTagIds,
      action
    },
    config
  );

  return true;
}
